<script module lang="ts">
  const typeMainProperty = {
    author: 'wdt:P50',
    publisher: 'wdt:P123',
    genre: 'wdt:P136',
    series: 'wdt:P179',
    collection: 'wdt:P195',
    edition: 'wdt:P629',
    translator: 'wdt:P655',
    subject: 'wdt:P921',
  } as const
</script>
<script lang="ts">
  import { getContext } from 'svelte'
  import { loadInternalLink } from '#app/lib/utils'
  import ImagesCollage from '#components/images_collage.svelte'
  import { usesSubEntitiesImages } from '#entities/components/lib/sub_entities_browser_helpers'
  import { omitClaims } from '#entities/components/lib/work_helpers'
  import type { SerializedEntitiesByUris, SerializedEntity } from '#entities/lib/entities'
  import type { PropertyUri } from '#server/types/entity'
  import BirthAndDeathDates from './birth_and_death_dates.svelte'
  import Infobox from './infobox.svelte'
  import type { Snippet } from 'svelte'

  const {
    entity,
    relatedEntities = {},
    showInfobox = true,
    listDisplay = false,
    isUriToDisplay = false,
    actions,
  }: {
    entity: SerializedEntity
    relatedEntities?: SerializedEntitiesByUris
    showInfobox?: boolean
    listDisplay?: boolean
    isUriToDisplay?: boolean
    actions?: Snippet
  } = $props()

  const label = $derived(entity.label)
  const image = $derived(entity.image)
  const images = $derived(entity.images)
  const pathname = $derived(entity.pathname)
  const serieOrdinal = $derived(entity.serieOrdinal)
  const subtitle = $derived(entity.subtitle)
  const title = $derived(entity.title)
  const type = $derived(entity.type)
  const uri = $derived(entity.uri)

  const layoutContext = getContext('layout-context')
  const prop: PropertyUri = typeMainProperty[layoutContext]

  const hasOnlyOneClaimValue = prop => entity.claims?.[prop]?.length === 1

  const claims = $derived.by(() => {
    if (prop && hasOnlyOneClaimValue(prop)) {
      // Remove main layout properties unless several claim values
      // ie. on the author layout, in the works section, the infobox will display authors claim
      // only if several authors are  defined, omit the author in the infobox if the author is the only one.
      return omitClaims(entity.claims, [ prop ])
    } else {
      return entity.claims
    }
  })
</script>
<div class="entity-list-row">
  <div class="entity-wrapper">
    <div
      class="cover"
      class:hasSubEntities={usesSubEntitiesImages(type)}
    >
      <a
        href={pathname}
        onclick={loadInternalLink}
        {title}
      >
        <ImagesCollage
          imagesUrls={images || [ image.url ]}
          limit={usesSubEntitiesImages(type) ? 4 : 1}
        />
      </a>
    </div>
    <div class="entity-info-line">
      <div class="entity-title">
        <h3>
          <a
            href={pathname}
            onclick={loadInternalLink}
            class="link"
            {title}
          >
            {#if layoutContext === 'serie' && serieOrdinal}
              {serieOrdinal}.
            {/if}
            {title || label}
          </a>
        </h3>
        {#if subtitle}
          <span class="subtitle">{subtitle}</span>
        {/if}
        {#if isUriToDisplay}
          <a
            href={pathname}
            target="_blank"
            rel="noreferrer"
            class="uri"
            onclick={loadInternalLink}
          >
            {uri}
          </a>
        {/if}
        {#if entity.type === 'human'}
          <BirthAndDeathDates {entity} />
        {/if}
      </div>
      {#if showInfobox}
        <div class="entity-details">
          <Infobox
            {claims}
            {relatedEntities}
            {listDisplay}
            shortlistOnly={true}
            entityType={type}
          />
        </div>
      {/if}
    </div>
  </div>
  {#if type === 'work'}
    {@render actions?.()}
  {/if}
</div>
<style lang="scss">
  @import "general/scss/utils";
  .entity-list-row{
    @include display-flex(row, center);
    background-color: white;
    inline-size: 100%;
    margin-block-end: 0.5em;
    padding: 0.5em;
  }
  .entity-wrapper{
    @include display-flex(row, center);
  }
  .entity-title{
    font-size: 1.1rem;
    line-height: 1.2rem;
    margin-block-end: 0.4rem;
    overflow: auto;
    max-height: 10em;
    .uri{
      @include shy;
      font-size: 0.9rem;
    }
  }
  .subtitle{
    line-height: 1rem;
    font-size: 0.9rem;
    color: $label-grey;
  }
  .cover{
    margin: 0 1em 0 0;
    :global(.images-collage){
      inline-size: 7em;
      block-size: 10em;
    }
  }
  .entity-info-line{
    flex: 1;
    margin: 0 1em;
  }
  h3{
    font-weight: normal;
    font-size: 1.1em;
    margin: 0;
  }

  /* Smaller screens */
  @media screen and (width < $smaller-screen){
    .entity-list-row{
      flex-wrap: wrap;
    }
    .entity-info-line{
      margin: 0.5em 0.5em 0.5em 0;
    }
  }
</style>
